import type { BlockDragger } from "../draggers/BlockDragger";
import { trackShapeChange } from "@/analytics/drawing-analytics";
import BlockLibrary from "@/blocks/library";
import type { BlockConfig } from "@/types/blocks.types";
import {
  createBlock,
  listDwgBlocks,
  listBlocksFromDatabase,
  importBlockIntoDatabase,
  listBlockDefinitionAttributes,
  type DwgBlockInfo,
  type BlockAttributeDefInfo,
} from "../blocks";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { selectionService } from "../selection";
import { isPhotoMarkerEntity } from "../photomarker/detection";
import {
  listBlockAttributes,
  setBlockAttribute,
  type BlockAttributeInfo,
} from "../blocks/attributes";
import { SetBlockAttributeCommand } from "../commands/SetBlockAttributeCommand";
import { Logger } from "@/logger";

export interface BlockManagerDeps {
  blockDragger: BlockDragger;
  viewer: DRDrawingViewer;
}

export class BlockManager {
  private blockDragger: BlockDragger;
  private viewer: DRDrawingViewer;
  private _templateDb: DrawingModule.OdDbDatabase | null = null;
  private _templateBlocks: DwgBlockInfo[] = [];

  constructor(deps: BlockManagerDeps) {
    this.blockDragger = deps.blockDragger;
    this.viewer = deps.viewer;
  }

  // ==================== Attributes (current selection) ====================

  /**
   * True when the selection is a single block reference carrying at least one
   * attribute. Photo markers are excluded: they have their own dedicated controls
   * in the selection menu.
   */
  canEditAttributesSelected(): boolean {
    const ids = selectionService.getAll();
    if (ids.length !== 1) return false;
    if (isPhotoMarkerEntity(this.viewer.module, ids[0])) return false;
    return listBlockAttributes(this.viewer.module, ids[0]).length > 0;
  }

  /** Attributes of the selected block reference, in definition order. */
  getSelectedAttributes(): BlockAttributeInfo[] {
    const ids = selectionService.getAll();
    if (ids.length !== 1) return [];
    return listBlockAttributes(this.viewer.module, ids[0]);
  }

  /**
   * Set the text of one attribute of the selected block, addressed by tag.
   * Returns false when nothing changed (unknown tag, identical value).
   */
  setSelectedAttribute(tag: string, value: string): boolean {
    const ids = selectionService.getAll();
    if (ids.length !== 1) return false;

    const params = setBlockAttribute(this.viewer.module, ids[0], tag, value);
    if (!params) return false;

    Logger.info(`BlockManager : attribute ${tag} = ${value}`);
    this.viewer.executeCommand(
      new SetBlockAttributeCommand(this.viewer, params),
    );
    trackShapeChange("block_attribute_changed");
    this.viewer.update();
    return true;
  }

  // ==================== Library Queries ====================

  get availableBlocks(): string[] {
    return BlockLibrary.getAllNames();
  }

  listDwgBlocks(): DwgBlockInfo[] {
    return listDwgBlocks(this.viewer);
  }

  /**
   * Editable (non-constant) attribute definitions of a block of the active drawing,
   * used by the pre-insert form of the blocks drawer.
   */
  listBlockDefinitionAttributes(blockName: string): BlockAttributeDefInfo[] {
    if (!this.viewer.database) return [];
    return listBlockDefinitionAttributes(
      this.viewer.module,
      this.viewer.database,
      blockName,
    );
  }

  /** Attribute values applied to the next inserts of the active block (keyed by tag). */
  setActiveBlockAttributeValues(values: Record<string, string> | null): void {
    this.blockDragger.attributeValues = values;
  }

  /** Values entered in the pre-insert form, or null when none were confirmed. */
  getActiveBlockAttributeValues(): Record<string, string> | null {
    return this.blockDragger.attributeValues;
  }

  // ==================== Template DWG ====================

  /**
   * Load an external DWG template from an ArrayBuffer.
   * Replaces any previously loaded template.
   * Returns the blocks found in the template.
   */
  loadTemplateFromBuffer(arrayBuffer: ArrayBuffer): DwgBlockInfo[] {
    this.clearTemplate();
    this._templateDb = this.viewer.openDatabaseFromBuffer(
      "__block_template.dwg",
      arrayBuffer,
    );
    this._templateBlocks = listBlocksFromDatabase(
      this.viewer.module,
      this._templateDb,
    );
    return this._templateBlocks;
  }

  get templateBlocks(): DwgBlockInfo[] {
    return this._templateBlocks;
  }

  get hasTemplate(): boolean {
    return this._templateDb !== null;
  }

  /**
   * Copy a block definition from the template into the active drawing database.
   * Returns false if no template is loaded or the block is not found.
   */
  importTemplateBlock(blockName: string): boolean {
    if (!this._templateDb || !this.viewer.database) return false;
    return importBlockIntoDatabase(
      this.viewer.module,
      this._templateDb,
      this.viewer.database,
      blockName,
    );
  }

  /**
   * Create a DR built-in block definition in the active drawing database.
   * No-op (returns true) if the block already exists.
   */
  importDRBlock(config: BlockConfig): boolean {
    if (!this.viewer.database) return false;
    createBlock(this.viewer, config);
    return true;
  }

  /**
   * Release the template database and clear the block list.
   */
  clearTemplate(): void {
    if (this._templateDb) {
      deleteObj(this._templateDb);
      this._templateDb = null;
    }
    this._templateBlocks = [];
  }

  // ==================== Active Block Configuration ====================

  get activeBlock(): string | null {
    return this.blockDragger.activeBlockName;
  }

  setActiveBlock(name: string): void {
    this.blockDragger.activeBlockName = name;
  }

  get scale(): number {
    return this.blockDragger.blockScale;
  }

  setScale(scale: number): void {
    this.blockDragger.blockScale = scale;
  }

  get rotation(): number {
    return this.blockDragger.blockRotation;
  }

  setRotation(radians: number): void {
    this.blockDragger.blockRotation = radians;
  }
}
